import React, { useState, useEffect } from 'react';
import { logger, LogEntry, LogLevel } from '../utils/logger';

export const DevPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<LogLevel | 'ALL'>('ALL');

  useEffect(() => {
    // Load initial logs
    setLogs(logger.getRecentLogs());

    // Listen for new log entries
    const handleLogEntry = (event: CustomEvent<LogEntry>) => {
      setLogs(prev => [...prev.slice(-49), event.detail]);
    };

    window.addEventListener('log-entry' as any, handleLogEntry);

    return () => {
      window.removeEventListener('log-entry' as any, handleLogEntry);
    };
  }, []);

  const filteredLogs = filter === 'ALL' 
    ? logs 
    : logs.filter(log => log.level === filter);

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case 'ERROR': return 'text-red-400';
      case 'WARN': return 'text-yellow-400';
      case 'INFO': return 'text-blue-400';
      case 'DEBUG': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  };

  const getLevelBgColor = (level: LogLevel) => {
    switch (level) {
      case 'ERROR': return 'bg-red-900/20';
      case 'WARN': return 'bg-yellow-900/20';
      case 'INFO': return 'bg-blue-900/20';
      case 'DEBUG': return 'bg-gray-900/20';
      default: return 'bg-gray-900/20';
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-[9998] bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded-lg shadow-lg border border-slate-600 flex items-center gap-2 text-sm"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        Dev Panel
        {logs.some(l => l.level === 'ERROR') && (
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] bg-slate-900 border-t border-slate-700 shadow-2xl">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center gap-4">
          <h3 className="text-sm font-semibold text-slate-300">Developer Panel</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('ALL')}
              className={`px-2 py-1 text-xs rounded ${filter === 'ALL' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              All ({logs.length})
            </button>
            <button
              onClick={() => setFilter('ERROR')}
              className={`px-2 py-1 text-xs rounded ${filter === 'ERROR' ? 'bg-red-600 text-white' : 'text-red-400 hover:text-red-300'}`}
            >
              Errors ({logs.filter(l => l.level === 'ERROR').length})
            </button>
            <button
              onClick={() => setFilter('WARN')}
              className={`px-2 py-1 text-xs rounded ${filter === 'WARN' ? 'bg-yellow-600 text-white' : 'text-yellow-400 hover:text-yellow-300'}`}
            >
              Warnings ({logs.filter(l => l.level === 'WARN').length})
            </button>
            <button
              onClick={() => setFilter('INFO')}
              className={`px-2 py-1 text-xs rounded ${filter === 'INFO' ? 'bg-blue-600 text-white' : 'text-blue-400 hover:text-blue-300'}`}
            >
              Info ({logs.filter(l => l.level === 'INFO').length})
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              logger.clearLogs();
              setLogs([]);
            }}
            className="px-2 py-1 text-xs text-slate-400 hover:text-white"
          >
            Clear
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="px-2 py-1 text-xs text-slate-400 hover:text-white"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      <div className="h-64 overflow-y-auto p-2 font-mono text-xs">
        {filteredLogs.length === 0 ? (
          <div className="text-center text-slate-500 py-8">No logs to display</div>
        ) : (
          <div className="space-y-1">
            {filteredLogs.map((log, index) => (
              <div
                key={`${log.cid}-${index}`}
                className={`p-2 rounded ${getLevelBgColor(log.level)} border border-slate-700`}
              >
                <div className="flex items-start gap-2">
                  <span className={`${getLevelColor(log.level)} font-semibold min-w-[50px]`}>
                    [{log.level}]
                  </span>
                  <span className="text-slate-500 min-w-[140px]">
                    {new Date(log.ts).toLocaleTimeString()}
                  </span>
                  <span className="text-slate-300 font-semibold">
                    {log.event}
                  </span>
                </div>
                {Object.keys(log.data).length > 0 && (
                  <div className="mt-1 ml-[200px] text-slate-400">
                    {JSON.stringify(log.data, null, 2)}
                  </div>
                )}
                <div className="mt-1 text-slate-600 text-[10px]">
                  CID: {log.cid}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};